<template>
  <AISuggestedContactsBasePanel
    ref="panel"
    assistant-panel
    :has-results="showResults && hasValidContacts"
    :selected-ids="selectedIds"
    :updating="updatingContacts"
    @add="handleAddContacts"
    @close="emit('close')"
  >
    <div class="tw:grid tw:gap-4 tw:px-4">
      <AIAssistantBaseChatRequest data-testid="chat-request">
        {{ getLocaleText("ai_assistant_panel.options.suggested_contacts") }}
      </AIAssistantBaseChatRequest>

      <AIAssistantBaseChatResponse
        data-testid="chat-response"
        :loading="loading"
      >
        <div class="tw:grid tw:gap-4">
          <AIAssistantBaseChatType
            :delay="500"
            @complete="updateChatState"
          >
            <template v-if="chatState === 'moment'">
              {{ getLocaleText("ai_assistant_panel.just_a_moment") }}
            </template>
            <template v-else-if="chatState === 'select'">
              {{ getLocaleText("ai_suggested_contacts_panel.type_prompt") }}
            </template>
          </AIAssistantBaseChatType>

          <AIAssistantBaseChatTransition
            v-if="showOptions"
            appear
            :disabled="disableTransitions"
          >
            <div class="tw:flex tw:gap-4">
              <BaseButton
                :active="selectedType === 'list'"
                active-color="selected"
                class="tw:flex-1 !tw:border-2"
                color="secondary"
                data-testid="list-button"
                :disabled="disableTypeButtons"
                icon="list"
                :label="
                  getLocaleText('ai_suggested_contacts_panel.list_button')
                "
                new-design
                new-icon
                @click="show('list')"
              />
              <BaseButton
                :active="selectedType === 'media_release'"
                active-color="selected"
                class="tw:flex-1 tw:whitespace-nowrap !tw:border-2"
                color="secondary"
                data-testid="media-release-button"
                :disabled="disableTypeButtons"
                icon="create"
                :label="
                  getLocaleText(
                    'ai_suggested_contacts_panel.media_release_button'
                  )
                "
                new-design
                new-icon
                @click="show('media_release')"
              />
              <BaseButton
                :active="selectedType === 'pitch'"
                active-color="selected"
                class="tw:flex-1 !tw:border-2"
                color="secondary"
                data-testid="pitch-button"
                :disabled="disableTypeButtons"
                icon="ai-generate"
                :label="
                  getLocaleText('ai_suggested_contacts_panel.pitch_button')
                "
                new-design
                new-icon
                @click="show('pitch')"
              />
            </div>
          </AIAssistantBaseChatTransition>
        </div>
      </AIAssistantBaseChatResponse>

      <AIAssistantBaseChatResponse
        v-if="selectedType"
        :loading="loading"
      >
        <div class="tw:grid tw:gap-4">
          <AIAssistantBaseChatType @complete="showListSelector = true">
            {{
              getLocaleText("ai_suggested_contacts_panel.select_type_prompt", {
                type: typeLabel,
              })
            }}
          </AIAssistantBaseChatType>

          <AIAssistantBaseChatTransition
            v-if="showListSelector"
            appear
            :disabled="disableTransitions"
          >
            <AISuggestedContactsListSelector
              v-if="selectedType === 'list'"
              :contact-list="selectedContactList"
              data-testid="lists-selector"
              @update:contact-list="handleContactListSelection"
            />
            <AISuggestedMediaReleaseSelector
              v-else
              data-testid="media-release-selector"
              :media-release="selectedMediaRelease"
              :type="selectedType"
              @update:media-release="handleMediaReleaseSelection"
            />
          </AIAssistantBaseChatTransition>
        </div>
      </AIAssistantBaseChatResponse>

      <template v-if="showResults">
        <AISuggestedContactsListResponse
          v-if="hasValidContacts"
          v-model:contact-type="contactOptions.contactType"
          v-model:selected-ids="selectedIds"
          v-model:sort-direction="contactOptions.sortDirection"
          v-model:sort-field="contactOptions.sortField"
          :contact-list="selectedContactList"
          :contacts="contacts"
          :loading="loadingContacts"
          :media-release="selectedMediaRelease"
          @chat-type-complete="showResultsList = true"
        />

        <AIAssistantBaseChatResponse v-if="showResultsPrompt">
          <div class="tw:grid tw:gap-4">
            <AIAssistantBaseChatType
              :disabled="showResetButtons"
              @complete="showResetButtons = true"
            >
              {{ resultsPrompt }}
            </AIAssistantBaseChatType>

            <AIAssistantBaseChatTransition
              v-if="showResetButtons"
              appear
            >
              <div class="tw:flex tw:gap-4">
                <BaseButton
                  class="tw:flex-1 tw:whitespace-nowrap"
                  color="secondary"
                  data-testid="different-list-button"
                  icon="list"
                  :label="
                    getLocaleText(
                      'ai_suggested_contacts_panel.different_list',
                      {
                        type: typeLabel,
                      }
                    )
                  "
                  new-design
                  new-icon
                  @click="restart"
                />
                <BaseButton
                  class="tw:flex-1"
                  color="secondary"
                  data-testid="start-over-button"
                  icon="circle-arrow"
                  :label="
                    getLocaleText('ai_suggested_contacts_panel.start_over', {
                      type: typeLabel,
                    })
                  "
                  new-design
                  new-icon
                  @click="reset"
                />
              </div>
            </AIAssistantBaseChatTransition>
          </div>
        </AIAssistantBaseChatResponse>
      </template>
    </div>
  </AISuggestedContactsBasePanel>
</template>

<script setup lang="ts">
import { promiseTimeout } from "@vueuse/core";
import { storeToRefs } from "pinia";
import {
  computed,
  nextTick,
  onMounted,
  reactive,
  ref,
  useTemplateRef,
  watch,
} from "vue";

import { track } from "shared/boot/analytics";
import { getLocaleText } from "shared/boot/i18n";
import AIAssistantBaseChatRequest from "shared/components/AI/AIAssistant/AIAssistantBaseChatRequest.vue";
import AIAssistantBaseChatResponse from "shared/components/AI/AIAssistant/AIAssistantBaseChatResponse.vue";
import AIAssistantBaseChatTransition from "shared/components/AI/AIAssistant/AIAssistantBaseChatTransition.vue";
import AIAssistantBaseChatType from "shared/components/AI/AIAssistant/AIAssistantBaseChatType.vue";
import AISuggestedContactsBasePanel from "shared/components/AI/AISuggestedContacts/AISuggestedContactsBasePanel.vue";
import AISuggestedContactsListResponse from "shared/components/AI/AISuggestedContacts/AISuggestedContactsListResponse.vue";
import AISuggestedContactsListSelector from "shared/components/AI/AISuggestedContacts/AISuggestedContactsListSelector.vue";
import AISuggestedMediaReleaseSelector from "shared/components/AI/AISuggestedContacts/AISuggestedMediaReleaseSelector.vue";
import { BaseButton } from "shared/components/base";
import useSuggestedContacts, {
  type SuggestedContactOptions,
} from "shared/composables/useSuggestedContacts";
import useSuggestedContactsSuggestions from "shared/composables/useSuggestedContactsSuggestions";
import { type ContactList, type MediaRelease } from "shared/resources";
import useAIStore from "shared/stores/ai";
import { type Nullable } from "shared/types/utility";

type Type = Nullable<"list" | "media_release" | "pitch">;

const emit = defineEmits(["close", "selected-ids"]);

const aiStore = useAIStore();

const { aiSuggestedContactIds, aiSuggestedContactsFilter } =
  storeToRefs(aiStore);

const contactOptions = reactive<SuggestedContactOptions>({
  contactType: "influencer",
  sortDirection: "desc",
  sortField: "relevance",
});

const { loadingContacts, contacts, loadContacts } =
  useSuggestedContacts(contactOptions);

const {
  contactList: selectedContactList,
  loading,
  mediaRelease: selectedMediaRelease,
  selectedIds,
  total: totalContactsForSelection,
  updating: updatingContacts,
  valid: hasValidContacts,
  add: addContacts,
  init: initContacts,
  reset: resetContacts,
} = useSuggestedContactsSuggestions();

const panel = useTemplateRef("panel");
const chatState = ref<string>("moment");
const showListSelector = ref<boolean>(false);
const showOptions = ref<boolean>(false);
const showResults = ref<boolean>(false);
const showResultsList = ref<boolean>(false);
const selectedType = ref<Type>(null);
const showResetButtons = ref<boolean>(false);
const disableTransitions = ref<boolean>(false);

const resultsPrompt = computed<string>(() => {
  if (hasValidContacts.value) {
    return getLocaleText("ai_suggested_contacts_panel.restart_prompt", {
      type: typeLabel.value,
    });
  }

  if (totalContactsForSelection.value) {
    return getLocaleText(
      "ai_suggested_contacts_panel.no_valid_contacts_prompt",
      {
        type: typeLabel.value,
      }
    );
  }

  return getLocaleText("ai_suggested_contacts_panel.no_contacts_prompt", {
    type: typeLabel.value,
  });
});

const showResultsPrompt = computed<boolean>(() => {
  if (hasValidContacts.value) {
    return showResultsList.value;
  }

  return !loadingContacts.value;
});

const typeLabel = computed<string>(() => {
  if (selectedType.value) {
    return getLocaleText(`ai_suggested_contacts_panel.${selectedType.value}`);
  }

  return "";
});

const disableTypeButtons = computed<boolean>(
  () => updatingContacts.value || loadingContacts.value
);

function updateChatState() {
  if (chatState.value === "select") {
    showOptions.value = true;
  } else {
    chatState.value = "select";
  }
}

function handleContactListSelection(contactList: Nullable<ContactList>) {
  aiSuggestedContactsFilter.value = {
    ...(contactList
      ? { suggestionsForContactListId: Number(contactList.id) }
      : {}),
  };

  loadSuggestedContacts();
}

function handleMediaReleaseSelection(mediaRelease: Nullable<MediaRelease>) {
  aiSuggestedContactsFilter.value = {
    ...(mediaRelease ? { suggestionsForMediaReleaseId: mediaRelease.id } : {}),
  };

  loadSuggestedContacts();
}

async function loadSuggestedContacts() {
  resetContacts();
  showResults.value = false;
  showResultsList.value = false;

  if (!Object.values(aiSuggestedContactsFilter.value).length) {
    return;
  }

  await initContacts();

  showResults.value = true;

  if (!hasValidContacts.value) {
    return;
  }

  loadContacts(aiSuggestedContactsFilter.value);
}

function show(selectType: Type) {
  resetContacts();
  selectedType.value = selectType;
  showResults.value = false;
  track(`Clicked ${typeLabel.value} from the General Assistant`);
}

async function restart() {
  disableTransitions.value = true;

  if (hasValidContacts.value && panel.value) {
    panel.value?.scrollToTop();
    await promiseTimeout(500);
  }

  show(selectedType.value);
  await nextTick();
  disableTransitions.value = false;
}

async function reset() {
  disableTransitions.value = false;

  if (hasValidContacts.value && panel.value) {
    panel.value?.scrollToTop();
    await promiseTimeout(500);
  }

  showOptions.value = false;
  selectedType.value = null;
  showResults.value = false;
  showListSelector.value = false;
  await nextTick();
  showOptions.value = true;
}

async function handleAddContacts() {
  await addContacts(typeLabel.value);
  loadContacts(aiSuggestedContactsFilter.value);
}

onMounted(() => {
  selectedIds.value = aiSuggestedContactIds.value;
});

watch(
  () => contactOptions.contactType,
  () => {
    loadContacts(aiSuggestedContactsFilter.value);
  }
);
</script>

<style scoped lang="scss">
@import "shared/css/components/ai.scss";

:deep(.base-button__selected) {
  --c-button-default: var(--s-color-dark-blue-1);
  --c-button-hover: var(--s-color-dark-blue-1);
  --c-button-active: var(--s-color-dark-blue-1);
  --c-button-border-default: var(--s-color-dark-blue-3);
  --c-button-border-hover: var(--s-color-dark-blue-3);
  --c-button-border-active: var(--s-color-dark-blue-3);
}
</style>
